// src/hooks/useRecipeActions.js
import { useState } from 'react';
import { useUpdateRecipeCounter } from '../Hooks/useUpdateRecipeCounter';
import { useNotification } from '../Context/notificationContext';

export const useRecipeActions = (recipe, refresh) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { incrementRecipeCounter } = useUpdateRecipeCounter();
  const { showNotification } = useNotification();

  const handleLike = async () => {
    if (!recipe?.id || isProcessing) return;
    
    try {
      setIsProcessing(true);
      await incrementRecipeCounter(recipe.id, 'likes');
      refresh();
      showNotification('success', 'Recipe liked successfully!');
    } catch (error) {
      console.error('Like error:', error);
      showNotification('error', error.message || 'Failed to like recipe');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleShare = async (platform) => {
    try {
      // Implement actual sharing logic based on platform
      const message = `Check out this recipe: ${recipe.recipe_name}\n${window.location.href}`;
      
      if (platform === 'copy') {
        await navigator.clipboard.writeText(message);
        showNotification('success', 'Link copied to clipboard!');
      } else {
        // Implement platform-specific sharing (e.g., Web Share API)
        if (navigator.share) {
          await navigator.share({
            title: recipe.recipe_name,
            text: message,
            url: window.location.href
          });
        }
      }
      
      await incrementRecipeCounter(recipe.id, 'shares');
      refresh();
    } catch (error) {
      if (error.name !== 'AbortError') {
        showNotification('error', 'Sharing failed. Please try again.');
      }
    }
  };

  const handleDownload = async () => {
    try {
      // Implement actual download logic
      await incrementRecipeCounter(recipe.id, 'downloads');
      refresh();
      showNotification('success', 'Recipe downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      showNotification('error', 'Failed to download recipe');
    }
  };

  return {
    handleLike,
    handleShare,
    handleDownload,
    isProcessing,
  };
};
